import { Card, Row, Spin, PageHeader, Col } from "antd";
import ErrorToDisplay from "components/error-handling/ErrorToDisplay";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { resetRatings, setTopics, setScoreAndValue } from "redux/reducers/RatingsSlice";
import { fetchSRSApplicants } from "redux/reducers/SRSApplicantSlice";
import { ApiURL, AppURL, getEnabled, getSourceQueryParams } from "utils/Actions";
import { API_RESPONSE_STATUS, RATING_APIS } from "utils/Constants";
import { showErrorModal } from "utils/hooks/handleNotification";
import { createGetRequest } from "utils/hooks/apiActions";
import { common } from "utils/Wording";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import avatarIcon from "assets/icons/avatar.svg";

import api from "ApiClient";
import { capitalize } from "lodash";
import style from "./style.module.css";
import { TableToolTip } from "components/common/table-title/tooltip";
import Restricted from "components/common/restricted";
import UACRatingForm from "./uac-rating-form";
import UTASRatingForm from "./utas-rating-form";
import { PERMISSIONS } from "permissions/app-permissions";
import { SRSApplicantType } from "utils/Types";
import useOpenDateConfigs from "utils/hooks/useOpenDateConfigs";

export const Ratings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { refNum: studentRefNumParam } = useParams<{ refNum: string }>();
  const {
    tableData: tableDataInRater,
    currentPage,
    filters,
    sorts,
    search,
    searchOrigin
  } = useAppSelector(state => state.srsApplicants);
  const { env } = useAppSelector(state => state.globalUI);
  const { whiteLabel } = useAppSelector(state => state.properties);
  const selectedStudent = tableDataInRater.find((e: SRSApplicantType) => {
    return e.refNum.toString() === studentRefNumParam;
  });

  const [isApiCallError, setIsApiCallError] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [ratingsInitialised, setRatingsInitialised] = useState(false);
  const isApiCallSuccess = !isApiLoading && selectedStudent && !isApiCallError;

  let originYear12Qual = "";

  const { srsOpen } = useOpenDateConfigs();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    dispatch(resetRatings());
    tableDataInRater.length === 0 && dispatch(fetchSRSApplicants({ source: env }));
  }, [env]);

  useEffect(() => {
    const enabled = getEnabled(env, whiteLabel);
    if (enabled && !ratingsInitialised) {
      callRatingApis();
    }
  }, [selectedStudent, whiteLabel, env, ratingsInitialised]);

  const handleError = (error?: any) => {
    if (error) {
      showErrorModal({
        title: t("httpRequestError"),
        errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
      });
    }
    setIsApiLoading(false);
    setIsApiCallError(true);
  };

  const callRatingApis = async () => {
    setIsApiLoading(true);
    if (selectedStudent) {
      setIsApiLoading(true);
      const getCallPassed = await initialiseAllRatingData();

      if (getCallPassed) {
        const createCallPassed = await createInitialRatings();
        if (createCallPassed) {
          setIsApiLoading(false);
          setIsApiCallError(false);
        } else {
          handleError();
        }
      } else {
        handleError();
      }
    } else {
      setIsApiLoading(false);
    }
  };

  const initialiseAllRatingData = async () => {
    const refNum = selectedStudent?.refNum;
    // calling three apis: /qualification/{refNum}, /rating/topics, /qualification/ratings/{refNum},
    const allGetPromise = Promise.all([
      createGetRequest({
        path: `${ApiURL.year12Qual}/${refNum}`,
        queryParams: env ? { source: env } : undefined,
        type: RATING_APIS.YEAR12,
        dataRequired: true
      }),
      createGetRequest({
        path: ApiURL.ratingTopics,
        queryParams: env ? { source: env } : undefined,
        type: RATING_APIS.TOPICS
      }),
      createGetRequest({
        path: `${ApiURL.ratingScores}/${refNum}`,
        queryParams: env ? { source: env } : undefined,
        type: RATING_APIS.SCORE
      })
    ]);

    try {
      const getResults: any[] = await allGetPromise;
      getResults.forEach(result => {
        if (result?.error) {
          throw new Error(common.loadingError);
        }
        updateRedux(result.type, result);
      });
      return true;
    } catch (error: any) {
      handleError(error);
      return false;
    } finally {
      setRatingsInitialised(true);
    }
  };

  const updateRedux = (type: string, result: any) => {
    switch (type) {
      case RATING_APIS.TOPICS:
        result?.data?.data ? dispatch(setTopics(result.data.data)) : dispatch(setTopics([]));
        return;
      case RATING_APIS.SCORE:
        result?.data?.data
          ? dispatch(setScoreAndValue(result.data.data))
          : dispatch(setScoreAndValue([]));
        return;
      case RATING_APIS.YEAR12:
        originYear12Qual = result?.data?.data?.origin;
        return;
      default:
        return false;
    }
  };

  const createInitialRatings = async () => {
    // calling /qualifications-srs to populate qualifications
    const refNum = selectedStudent?.refNum;
    const enabled = getEnabled(env, whiteLabel);
    if (!enabled) {
      return;
    }
    const queryParams = getSourceQueryParams(env, whiteLabel);
    try {
      const res = await api.post(
        ApiURL.qualification,
        {
          refNum,
          origin: originYear12Qual
        },
        queryParams
      );
      const { status } = await res?.data;
      if (status === API_RESPONSE_STATUS.SUCCESS) {
        return true;
      }
    } catch (error: any) {
      handleError(error);
      return false;
    }
  };

  const backToApplicationTable = () => {
    const criteriaObj = { currentPage, filters, sorts, search, searchOrigin };
    history.push(`${AppURL.school.rater.srsApplications}`, criteriaObj);
  };

  if (!srsOpen) {
    return <></>;
  }

  return (
    <>
      <Helmet>
        <title>{t("helmet.ratings.title")}</title>
        <meta name="description" content={t("helmet.ratings.meta") || ""} />
      </Helmet>

      <Row>
        <Col span={24} xl={18}>
          {isApiLoading ? (
            <Spin size="large" className="center-content" style={{ height: "50vh" }} />
          ) : isApiCallSuccess ? (
            <>
              <div className="gap-section">
                <PageHeader
                  className="site-page-header display-lar-typo"
                  onBack={backToApplicationTable}
                  title={
                    <h1 className={style.heading}>
                      {t("rating.title")} {capitalize(selectedStudent?.givenName1)}{" "}
                      {capitalize(selectedStudent?.familyName)}
                    </h1>
                  }
                  style={{ padding: 0 }}
                />
                <p className="bottom-zero display-p-typo">{t("rating.desc")}</p>
              </div>
              <Card style={{ background: "#15506F" }}>
                <p className={style["brand-color-text"]}>{t("rating.applicantCardTitle")}</p>
                <div className={style["student-card-content"]}>
                  <img src={avatarIcon} alt="" style={{ marginRight: 20 }} />
                  <div style={{ color: "white" }}>
                    <h3 className={style["brand-color-text"]} style={{ marginBottom: 0 }}>
                      {selectedStudent.fullName}
                    </h3>
                    {selectedStudent?.studentNumber && (
                      <div className={style["brand-color-text"]}>
                        <div style={{ marginRight: 4 }}>{t("rating.studentID")}</div>
                        <TableToolTip
                          iconColor="white"
                          title={"students.studentID"}
                          altText={"students.studentID"}
                        />
                        <div style={{ marginLeft: 4 }}>{selectedStudent?.studentNumber}</div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <Restricted to={PERMISSIONS.RATINGS.UAC_RATING_FORM}>
                <UACRatingForm
                  handleError={handleError}
                  backToApplicationTable={backToApplicationTable}
                />
              </Restricted>
              <Restricted to={PERMISSIONS.RATINGS.UTAS_RATING_FORM}>
                <UTASRatingForm
                  handleError={handleError}
                  backToApplicationTable={backToApplicationTable}
                />
              </Restricted>
            </>
          ) : (
            <ErrorToDisplay title={t("loadingError")} />
          )}
        </Col>
        <Col span={0} xl={6}></Col>
      </Row>
    </>
  );
};

export default Ratings;
